import { useDispatch, useSelector } from "react-redux";
import ProductCategoryService from "../services/ProductCategoryService";
import useAlerts from "./useAlerts";
import {
    loadingProductCategories,
    deactivateStateProductCategory,
    updateProductCategory,
    addProductCategory,
    loadingProductCategoriesDropdown,
} from "../store/slices/productCategory/productCategorySlice";
import type { RootState, AppDispatch } from "../store/store";
import useLoading from "./useLoading";
import useApiErrorHandler from "./useApiErrorHandler";
import useModals from "./useModals";

export const useProductCategories = () => {
    const { show: showAlert } = useAlerts();
    const { openModal } = useModals();

    const dispatch: AppDispatch = useDispatch();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();

    // Estados del store
    const { productCategories, productCategoriesDropdown, productCategorySelected } = useSelector(
        (state: RootState) => state.productCategory
    );

    const handlerSearchByType = async ({ searchType, name }) => {
        try {
            showLoadingIndicator(); // Mostrar el modal de carga

            let result;
            switch (searchType) {
                case 'all':
                    result = await ProductCategoryService.getAll();
                    dispatch(loadingProductCategories(result)); // Cargar todas las categorías
                    break;
                case 'name':
                    result = await ProductCategoryService.findByName(name);

                    dispatch(loadingProductCategories(result?.data || [])); // Si `data` no existe, enviamos un array vacío
                    break;
                default:
                    throw new Error(`Tipo de búsqueda no válido: ${searchType}`);
            }

            // console.log("Resultado de la búsqueda:", result);

        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator(); // Ocultar el modal de carga al finalizar la solicitud
        }
    };


    /**
     * Obtener todas las categorías.
     */
    const fetchProductCategories = async () => {
        try {
            showLoadingIndicator();
            const response = await ProductCategoryService.getAll();
            dispatch(loadingProductCategories(response));
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
    * Obtener categorías de productos para el dropdown.
    */
    const fetchProductCategoriesDropdown = async () => {
        try {
            const response = await ProductCategoryService.getDropdown();
            dispatch(loadingProductCategoriesDropdown(response));
        } catch (error) {
            handle(error);
        }
    };

    const handlerAddProductCategory = async (data: { id: number; name: string }) => {
        try {
            if (data.id === 0) {
                const { id, ...categoryData } = data;
                const response = await ProductCategoryService.save(categoryData);
                const { title, message, type, data: newCategory } = response;

                if (newCategory) {
                    dispatch(addProductCategory(newCategory)); // ✅ Agregar la nueva categoría a Redux
                }

                openModal(type, { title, message, });
            } else {
                const response = await ProductCategoryService.update(data.id, data.name);
                const { type, title, message, data: updatedCategory } = response;

                if (updatedCategory) {
                    dispatch(updateProductCategory(updatedCategory)); // ✅ Actualizar la categoría en Redux
                }

                openModal(type, { title, message, });
            }
        } catch (error: any) {
            handle(error);
        }
    };


    const toggleProductCategoryStatus = async (id: number) => {
        try {
            const response = await ProductCategoryService.deactivate(id);

            // ✅ Asegurar que response tenga datos antes de acceder a ellos
            const title = response?.title ?? "Operación completada";
            const message = response?.message ?? "El estado de la categoría de producto ha sido actualizado.";
            const type = response?.type ?? "success";

            showAlert(type, title, message);

            // ✅ Asegurar que response.data es válido antes de actualizar el estado
            dispatch(deactivateStateProductCategory(response?.data ?? {}));
        } catch (error: any) {
            handle(error);
        }
    };


    return {
        handlerSearchByType,
        productCategories,
        fetchProductCategoriesDropdown,
        productCategoriesDropdown,
        productCategorySelected,
        fetchProductCategories,
        handlerAddProductCategory,
        toggleProductCategoryStatus,
    };
};
